<!-- app/components/home/FilterBar.vue -->
<script lang="ts" setup>
import type { SectionType } from '~/utils/api/common';
import type { ProductTag } from '~/utils/tags';

const props = defineProps<{
  sections: SectionType[];
  tags: ProductTag[];
  activeSectionCode?: string;
  activeTagSlugs: string[];
  /** Чекбокс «Товары по акции 2=3» показываем только пока акция есть (есть участники в выдаче). */
  showTwoPlusOneFilter?: boolean;
  twoPlusOne?: boolean;
}>();

const emit = defineEmits<{
  (event: 'update:activeSectionCode', value: string | undefined): void;
  (event: 'update:activeTagSlugs', value: string[]): void;
  (event: 'update:twoPlusOne', value: boolean): void;
}>();

const { t } = useI18n();

const ALL = 'all';

const sectionOptions = computed(() => [
  { value: ALL, label: t('category_all') },
  ...props.sections
    .filter((section) => !section.parentId)
    .map((section) => ({ value: section.code, label: section.name })),
]);

const sectionValue = computed(() => props.activeSectionCode ?? ALL);

const onChangeSection = (value: string | number | string[]) => {
  emit('update:activeSectionCode', value === ALL ? undefined : String(value));
};

const tagOptions = computed(() => props.tags.map((tag) => ({ value: tag.slug, label: tag.label })));

const onChangeTags = (value: string | number | string[]) => {
  emit('update:activeTagSlugs', Array.isArray(value) ? value : []);
};

const removeTag = (slug: string) => {
  emit(
    'update:activeTagSlugs',
    props.activeTagSlugs.filter((item) => item !== slug),
  );
};

const activeTagLabels = computed(() =>
  props.activeTagSlugs
    .map((slug) => props.tags.find((tag) => tag.slug === slug))
    .filter((tag): tag is ProductTag => Boolean(tag)),
);

const onToggleTwoPlusOne = (value: boolean) => {
  emit('update:twoPlusOne', value);
};
</script>

<template>
  <div class="filterBar" data-test-id="filter-bar">
    <div class="filterBar__row">
      <BaseFilterSelect
        mode="single"
        :model-value="sectionValue"
        :options="sectionOptions"
        :trigger-label="t('category_all')"
        data-test-id="filter-section"
        @update:model-value="onChangeSection"
      />

      <BaseFilterSelect
        v-if="tags.length"
        mode="multi"
        :model-value="activeTagSlugs"
        :options="tagOptions"
        :trigger-label="t('filter_skin_needs')"
        :apply-label="t('filter_apply')"
        data-test-id="filter-tags"
        @update:model-value="onChangeTags"
      />

      <!-- Тоггл акции 2=3 — пилюля в стиле бейджа карточки: неактивен оранжевый
           контур, активен — залитый. Полное описание фильтра — в aria-label. -->
      <button
        v-if="showTwoPlusOneFilter"
        type="button"
        class="filterBar__twoPlusOnePill"
        :class="{ filterBar__twoPlusOnePill_active: twoPlusOne }"
        :aria-pressed="twoPlusOne"
        :aria-label="t('filter_two_plus_one')"
        :data-test-id="`filter-2plus1`"
        @click="onToggleTwoPlusOne(!twoPlusOne)"
      >
        {{ t('promo_label') }}
      </button>
    </div>

    <ul v-if="activeTagLabels.length" class="filterBar__chips">
      <li v-for="tag in activeTagLabels" :key="tag.slug" class="filterBar__chip" data-test-id="filter-tag-chip">
        <span class="filterBar__chipLabel">{{ tag.label }}</span>
        <button
          type="button"
          class="filterBar__chipRemove"
          :data-test-id="`filter-tag-chip-remove-${tag.slug}`"
          :aria-label="t('filter_remove_tag')"
          @click="removeTag(tag.slug)"
        >
          <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -- decorative glyph; button is labelled via aria-label -->
          <span class="filterBar__chipIcon" aria-hidden="true">×</span>
        </button>
      </li>
    </ul>
  </div>
</template>

<style lang="scss">
.filterBar {
  $self: &;

  &__row {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: center;
  }

  &__twoPlusOnePill {
    width: 104px;
    height: 42px;
    border-radius: 21px;
    border: 1px solid #ff9d6c;
    background: transparent;
    color: #ff9d6c;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    font-size: 16px;
    letter-spacing: 0.2px;
    cursor: pointer;
    transition:
      background-color 0.3s ease,
      color 0.3s ease;

    &_active {
      // Активен: заливка 20% от цвета акции, бордер и текст остаются насыщенными.
      background-color: rgba(255, 157, 108, 0.2);
      color: #ff9d6c;
    }

    @include mq('sm') {
      width: 85px;
      height: 40px;
    }
  }

  &__chips {
    list-style: none;
    margin: 12px 0 0;
    padding: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  &__chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    box-sizing: border-box;
    height: 38px;
    padding: 6px 12px;
    background: #c0c0c0;
    border-radius: 38px;
    font-size: 13px;
    color: #fff;
  }

  &__chipLabel {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__chipRemove {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 0;
    flex-shrink: 0;
    color: #fff;
  }

  &__chipIcon {
    font-size: 16px;
    line-height: 1;
    color: #fff;
  }

  @include mq('sm') {
    &__row {
      gap: 8px;
    }
  }
}
</style>

<i18n>
ru:
  category_all: Все продукты
  filter_skin_needs: Потребности кожи
  filter_apply: Применить
  filter_remove_tag: Убрать фильтр
  filter_two_plus_one: Товары по акции 2=3
  # Название акции — не переводится, ключ нужен только чтобы обойти no-raw-text
  promo_label: '2=3'
en:
  category_all: All products
  filter_skin_needs: Skin needs
  filter_apply: Apply
  filter_remove_tag: Remove filter
  filter_two_plus_one: 2=3 promo items
  promo_label: '2=3'
</i18n>
